import React, { useState, useEffect, useCallback } from 'react';
import { Pin } from 'lucide-react';
import { getPinnedFields } from '../api/pinnedFields';
import PinFieldManager from './PinFieldManager';

/**
 * Renders the current user's pinned fields for one record.
 * Filters out pins whose visibility rules are NOT satisfied for THIS record's
 * data — that filtering already happened server-side, so we just intersect the
 * pin list with profile.ordered_fields.
 *
 * Listens for the 'refresh-pinned-fields' window event so the AI's MANAGE_PINS
 * intent (and the pin manager modal) can trigger a reload.
 *
 * Props:
 *   recordTypeId:   UUID of the record's type
 *   orderedFields:  visibility-filtered field list from getCaseProfile (new return)
 *   fieldData:      legacy { name: value } map — used as fallback if orderedFields is empty
 */
export default function PinnedFieldsSection({ recordTypeId, orderedFields, fieldData }) {
  const [pins, setPins] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);

  const loadPins = useCallback(async () => {
    if (!recordTypeId) return;
    setLoadError(null);
    try {
      const data = await getPinnedFields(recordTypeId);
      setPins(data);
    } catch (err) {
      console.error('[PinnedFields] load failed', err);
      setLoadError(err.response?.data?.error || 'Failed to load pinned fields');
      setPins({ source: 'error', pin_cap: 15, pins: [] });
    }
  }, [recordTypeId]);

  useEffect(() => { loadPins(); }, [loadPins]);

  // Refresh from MANAGE_PINS chat intent or other dispatchers
  useEffect(() => {
    const handler = () => loadPins();
    window.addEventListener('refresh-pinned-fields', handler);
    return () => window.removeEventListener('refresh-pinned-fields', handler);
  }, [loadPins]);

  if (!recordTypeId) return null;
  if (loadError) {
    return (
      <div className="text-xs text-red-400 px-1 py-1">
        {loadError}
        <button onClick={() => loadPins()} className="ml-2 underline text-indigo-400 hover:text-indigo-300">
          Retry
        </button>
      </div>
    );
  }
  if (!pins) return <p className="text-xs text-gray-500 px-3 py-2">Loading pinned fields…</p>;

  // Map from field_definition_id → orderedFields entry (visibility-filtered)
  const orderedById = {};
  for (const f of (orderedFields || [])) orderedById[f.id] = f;
  const visibleIds = new Set(Object.keys(orderedById));

  // Display = pins that are also visible. Skip pins whose visibility rules
  // didn't pass for this record (e.g. attorney-only fields on a non-attorney).
  const display = pins.pins
    .filter(p => visibleIds.has(p.field_definition_id))
    .map(p => orderedById[p.field_definition_id])
    .filter(Boolean);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-gray-500 uppercase tracking-wide">
          {display.length}/{pins.pins.length} pinned · {pins.source}
        </span>
        <button
          onClick={() => setManagerOpen(true)}
          className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300"
        >
          <Pin className="h-3 w-3" /> Manage
        </button>
      </div>

      {display.length === 0 ? (
        <p className="text-xs text-gray-500">
          {pins.pins.length === 0
            ? 'No pins set — showing defaults by sort order.'
            : 'No pinned fields visible for this record.'}
        </p>
      ) : (
        <div className="space-y-1">
          {display.map(f => (
            <div key={f.id} className="flex justify-between gap-2 text-xs border-b border-gray-700/40 pb-1">
              <span className="text-gray-400 flex-shrink-0" style={{ minWidth: '40%' }}>{f.label || f.name}</span>
              <span
                className="text-right break-words"
                style={{ color: (f.value === null || f.value === undefined || f.value === '') ? '#4b5563' : '#e5e7eb' }}
              >
                {formatValue(f.value, f.field_type)}
              </span>
            </div>
          ))}
        </div>
      )}

      {managerOpen && (
        <PinFieldManager
          recordTypeId={recordTypeId}
          currentPins={pins.pins.map(p => p.field_definition_id)}
          onClose={() => setManagerOpen(false)}
          onSaved={() => { setManagerOpen(false); loadPins(); }}
        />
      )}
    </div>
  );
}

function formatValue(val, type) {
  if (val === null || val === undefined || val === '') return '—';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  if (type === 'CURRENCY') {
    const n = Number(val);
    return Number.isFinite(n) ? '$' + n.toLocaleString() : String(val);
  }
  if (type === 'TOGGLE') return val ? 'Yes' : 'No';
  if (type === 'DATE') {
    try { return new Date(val).toLocaleDateString(); } catch { return String(val); }
  }
  return String(val);
}
